import React from 'react';
import './ReactTableStyles.scss'
import ReactBaseTable, {DefaultTableProps, FilterOptions} from "./ReactBaseTable";
import {AsyncListInput} from "../../sharedmodels/asyncList/asyncListInput";
import {GroupingState, SortingState} from "@tanstack/react-table";
import {AsyncListSortOrderInput} from "../../sharedmodels/asyncList/asyncListSortOrderInput";

type AsyncTableProps<T extends object, D extends AsyncListInput> = {
    asyncListInput: Readonly<AsyncListInput>;
    setAsyncListInput: React.Dispatch<React.SetStateAction<D>>
    totalRecords: number;
    filteredRecords: number;
    isLoading: boolean;
    pageSizeOptions?: number[];
    searchPlaceholder?: string;
    hidePageSizeFilter?: boolean;
    hideSearchLabel?: boolean;
    hideColumnSettingsFilter?: boolean;
    renderAdditionalFilterItems?: JSX.Element;
    enableColumnResizing?: boolean;
    enableColumnGrouping?: boolean;
    initialSortColumn?: string;
    defaultExpandedGroups?: boolean;
    getSubRows?: (rowData: T) => T[] | undefined;
} & DefaultTableProps<T>;

const ReactAsyncTable = <T extends object, D extends AsyncListInput>({
     tableId,
     data,
     columns,
     asyncListInput,
     setAsyncListInput,
     totalRecords,
     filteredRecords,
     isLoading,
     hidePageSizeFilter = false,
     hideSearchLabel = false,
     renderAdditionalFilterItems,
     enableColumnGrouping = false,
     enableColumnResizing = false,
     hideColumnSettingsFilter = false,
     initialSortColumn = asyncListInput.order[0]?.columnName,
     pageSizeOptions = [10, 25, 50],
     searchPlaceholder,
     defaultExpandedGroups = true,
     getSubRows,
     ...defaultProps
 }: AsyncTableProps<T, D>) => {
    
    const handlePageChange = (page: number) => {
        setAsyncListInput((prev) => ({...prev, start: ((page - 1) * prev.length), draw: page - 1}));
    };

    const handleSorting = (sortInfo: SortingState) => {
        setAsyncListInput((prev) => ({
            ...prev, order: sortInfo.map((x) => {
                return {
                    columnName: x.id,
                    dir: x.desc ? "desc" : "asc",
                } as AsyncListSortOrderInput;
            })
        }));
    };

    const handleGrouping = (groupingInfo: GroupingState) => {
        setAsyncListInput((prev) => ({
            ...prev, grouping: groupingInfo.map((x) => {
                return {
                    columnName: x,
                };
            })
        }));
    };

    const handleSearch = (search: string) => {
        setAsyncListInput((prev) => ({...prev, search: {value: search}, start: 0, draw: 1}));
    };

    const handlePageSize = (pageSize: number) => {
        setAsyncListInput((prev) => ({...prev, length: pageSize, start: 0, draw: 1}));
    };

    const filterOptions: FilterOptions = {
        hidePageSizeFilter: hidePageSizeFilter,
        hideColumnSettingsButton: hideColumnSettingsFilter,
        hideSearchLabel: hideSearchLabel,
        searchPlaceholder: searchPlaceholder,
        pageSizeOptions: pageSizeOptions,
        onPageSizeChangedCallback: handlePageSize,
        onSearchChangeCallback: handleSearch,
        initialSortColumn: initialSortColumn,
        renderAdditionalFiltersItems: renderAdditionalFilterItems
    };

    return (
        <ReactBaseTable
            {...defaultProps}
            tableId={tableId}
            data={data}
            columns={columns}
            isAsync
            filterOptions={filterOptions}
            isLoading={isLoading}
            enableColumnGrouping={enableColumnGrouping}
            enableColumnResizing={enableColumnResizing}
            onSortCallback={handleSorting}
            onGroupingCallback={handleGrouping}
            getSubRows={getSubRows}
            defaultExpandedGroups={defaultExpandedGroups}
            paginationOverride={{
                pageIndex: (Math.floor(asyncListInput.start / asyncListInput.length) + 1),
                pageLength: asyncListInput.length,
                totalRecords: totalRecords,
                filteredRecords: filteredRecords,
                onPageChangeCallback: handlePageChange,
            }}
        />
    );
};

export default ReactAsyncTable;
